// src/scenes/groupOfficials/form.jsx
import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Alert,
  Divider,
} from "@mui/material";

const GroupOfficialsForm = ({
  open,
  onClose,
  onSubmit,
  saving,
  errorMsg,
  positions,
  users,
  assignmentsMap,
  setAssignmentsMap,
}) => {
  // ✅ Friendly labels
  const userOptions = useMemo(() => {
    return (users || [])
      .filter((u) => u?.isActive !== false) // optional: hide inactive users
      .map((u) => {
        const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
        return {
          id: String(u.id),
          label: name || u.email || `User #${u.id}`,
          meta: u.role ? ` • ${u.role}` : "",
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [users]);

  const positionRows = useMemo(() => {
    return [...(positions || [])].sort((a, b) =>
      (a?.positionName || "").localeCompare(b?.positionName || "")
    );
  }, [positions]);

  const handleChange = (positionId, userId) => {
    setAssignmentsMap((prev) => ({
      ...prev,
      [String(positionId)]: userId ? String(userId) : "",
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Assign Group Officials</DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Select a member for each position. Saving will create/update assignments in bulk.
        </Typography>

        {errorMsg ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMsg}
          </Alert>
        ) : null}

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: "grid", gap: 2 }}>
          {positionRows.map((p) => (
            <Box
              key={p.id}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1.2fr" },
                gap: 2,
                alignItems: "center",
              }}
            >
              <Box>
                <Typography fontWeight={800}>{p.positionName}</Typography>
                {p.description ? (
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {p.description}
                  </Typography>
                ) : null}
              </Box>

              <FormControl fullWidth disabled={saving}>
                <InputLabel id={`pos-${p.id}-label`}>Member</InputLabel>
                <Select
                  labelId={`pos-${p.id}-label`}
                  label="Member"
                  value={assignmentsMap?.[String(p.id)] || ""}
                  onChange={(e) => handleChange(p.id, e.target.value)}
                >
                  <MenuItem value="">
                    <em>— Select Member —</em>
                  </MenuItem>
                  {userOptions.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.label}
                      {u.meta}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSubmit} disabled={saving}>
          {saving ? "Saving..." : "Save Assignments"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupOfficialsForm;